import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  Modal
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { AuthContext } from '../contexts/AuthContext';
import { SheetContext } from '../contexts/SheetContext';
import sheetService from '../services/sheetService';
import alertService from '../services/alertService';
import hapticService from '../services/hapticService';
import LoadingScreen from '../components/LoadingScreen';
import { useRetryCountdown } from '../hooks/useRetryCountdown';
import { config } from '../config';
import AsyncStorageHelper from '../services/asyncStorageHelper';

const SelectionScreen = ({ route, navigation }) => {
  const { competitionName } = route.params;
  const { user, getValidAccessToken, signOut } = useContext(AuthContext);
  const { sheetId } = useContext(SheetContext);
  
  const [riders, setRiders] = useState([]);
  const [filteredRiders, setFilteredRiders] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [lapsAndSections, setLapsAndSections] = useState({ laps: 0, sections: 0, availableSections: [] });
  
  // États pour la gestion de section
  const [assignedSection, setAssignedSection] = useState(null);
  const [showSectionSelection, setShowSectionSelection] = useState(false);
  const [isCheckingSection, setIsCheckingSection] = useState(true);
  const [isLoadingCourseData, setIsLoadingCourseData] = useState(false);
  const [showSectionChangeModal, setShowSectionChangeModal] = useState(false);
  
  // 🆕 Variable pour éviter les vérifications multiples
  const [hasCheckedSectionValidity, setHasCheckedSectionValidity] = useState(false);

  // 🆕 États pour la progression des pilotes
  const [ridersProgress, setRidersProgress] = useState({});
  const [progressRefreshInterval, setProgressRefreshInterval] = useState(null);

  // Hook pour le countdown du retry
  const { getButtonText, isDisabled, startCountdown, resetCountdown } = useRetryCountdown(config.RETRY_COUNTDOWN_SECONDS);

  // Reset search when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      setSearchText('');
    }, [])
  );

  // 🆕 Fonction pour récupérer la progression des pilotes
  const fetchRidersProgress = useCallback(async () => {
    if (!sheetId || !competitionName || !getValidAccessToken) return;
    
    try {
      const progressData = await sheetService.getRidersProgress(
        sheetId,
        competitionName,
        getValidAccessToken
      );
      
      setRidersProgress(progressData);
    } catch (e) {
      console.error('Error fetching riders progress:', e);
      // En cas d'erreur, on ne fait rien pour ne pas perturber l'affichage principal
    }
  }, [sheetId, competitionName, getValidAccessToken]);

  // Charger les données de la course d'abord
  const loadCourseData = useCallback(async () => {
    if (!sheetId || !user || !competitionName || !getValidAccessToken) return false;
    
    setIsLoadingCourseData(true);
    try {
      const courseData = await sheetService.getRiderData(
        sheetId,
        competitionName,
        getValidAccessToken
      );
      
      setLapsAndSections({ 
        laps: courseData.laps, 
        sections: courseData.sections,
        availableSections: courseData.availableSections || Array.from({length: courseData.sections}, (_, i) => i + 1)
      });

      // 🔧 CORRECTION: Ne vérifier qu'une seule fois
      if (!hasCheckedSectionValidity && assignedSection && courseData.availableSections && 
          !courseData.availableSections.includes(assignedSection)) {
        console.log(`Section S${assignedSection} not available, opening selection modal`);
        setShowSectionSelection(true);
        setHasCheckedSectionValidity(true);
      }

      console.log(`Course data loaded: ${courseData.laps} laps, ${courseData.sections} sections`);
      return true;
    } catch (e) {
      console.error('Error loading course data:', e);
      return false;
    } finally {
      setIsLoadingCourseData(false);
    }
  }, [sheetId, competitionName, user, getValidAccessToken, assignedSection, hasCheckedSectionValidity]);

  // Vérifier la section assignée au chargement
  useEffect(() => {
    const checkAssignedSection = async () => {
      if (!config.ENABLE_SECTION_MEMORY) {
        setIsCheckingSection(false);
        return;
      }
      
      const courseLoaded = await loadCourseData();
      if (!courseLoaded) {
        setIsCheckingSection(false);
        return;
      }
      
      try {
        const lastSection = await AsyncStorageHelper.getItem('lastSelectedSection');
        
        if (lastSection !== null && lastSection !== undefined) {
          const sectionNum = parseInt(lastSection, 10);
          
          if (!isNaN(sectionNum) && sectionNum >= 1) {
            setAssignedSection(sectionNum);
            console.log('Found assigned section:', sectionNum);
          } else {
            console.warn('Invalid assigned section:', lastSection);
            setShowSectionSelection(true);
          }
        } else {
          console.log('No assigned section found, showing selection');
          setShowSectionSelection(true);
        }
      } catch (error) {
        console.error('Error checking assigned section:', error);
        setShowSectionSelection(true);
      } finally {
        setIsCheckingSection(false);
      }
    };
    
    checkAssignedSection();
  }, [loadCourseData]);

  // Démarrer le countdown seulement pour les erreurs non-session
  useEffect(() => {
    if (error && errorType && errorType !== 'session') {
      startCountdown();
    } else {
      resetCountdown();
    }
  }, [error, errorType, startCountdown, resetCountdown]);
  
  // Fetch riders for the selected competition
  const fetchRiders = useCallback(async () => {
    if (!sheetId || !user || !competitionName || !getValidAccessToken) return;
    
    setIsLoading(true);
    setError(null);
    setErrorType(null);
    
    try {
      const ridersData = await sheetService.getRiders(
        sheetId,
        competitionName,
        getValidAccessToken
      );
      
      const sortedRiders = ridersData.sort((a, b) => {
        const numA = parseInt(a.number, 10) || 0;
        const numB = parseInt(b.number, 10) || 0;
        return numA - numB;
      });
      
      setRiders(sortedRiders);
      setFilteredRiders(sortedRiders);
      
      // 🔧 CORRECTION: Ne pas refaire la vérification ici pour éviter le double modal
      
    } catch (e) {
      console.error('Error fetching riders:', e);
      
      if (e instanceof sheetService.ApiError) {
        setErrorType(e.type);
      } else {
        setErrorType('network');
      }
      
      setError('Failed to fetch riders');
      hapticService.error();
    } finally {
      setIsLoading(false);
    }
  }, [sheetId, competitionName, user, getValidAccessToken]);
  
  useEffect(() => {
    if (!showSectionSelection && !isCheckingSection) {
      fetchRiders();
    }
  }, [fetchRiders, showSectionSelection, isCheckingSection]);

  // 🆕 Auto-refresh de la progression des pilotes
  useEffect(() => {
    if (!showSectionSelection && !isCheckingSection && riders.length > 0) {
      // Récupérer immédiatement
      fetchRidersProgress();
      
      // Configurer l'intervalle de mise à jour (toutes les 10 secondes)
      const intervalId = setInterval(fetchRidersProgress, 10000);
      setProgressRefreshInterval(intervalId);
      
      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }
  }, [fetchRidersProgress, showSectionSelection, isCheckingSection, riders.length]);

  // 🆕 Nettoyer l'intervalle quand on quitte l'écran
  useEffect(() => {
    return () => {
      if (progressRefreshInterval) {
        clearInterval(progressRefreshInterval);
      }
    };
  }, [progressRefreshInterval]);
  
  // Filter riders based on search
  useEffect(() => {
    if (!searchText) {
      setFilteredRiders(riders);
    } else {
      const filtered = riders.filter(
        rider =>
          rider.name.toLowerCase().includes(searchText.toLowerCase()) ||
          rider.number.toString().includes(searchText)
      );
      setFilteredRiders(filtered);
    }
  }, [searchText, riders]);

  // Reset search when screen comes into focus AND check for section updates
  useFocusEffect(
    useCallback(() => {
      // Reset search text (existant)
      setSearchText('');
      
      // 🆕 Récupérer la progression immédiatement quand on revient sur l'écran
      if (riders.length > 0) {
        fetchRidersProgress();
      }
      
      // 🆕 Vérifier si le secteur assigné a été modifié dans ScoringScreen
      const checkSectionUpdate = async () => {
        if (!config.ENABLE_SECTION_MEMORY) return;
        
        try {
          const currentStoredSection = await AsyncStorageHelper.getItem('lastSelectedSection');
          
          if (currentStoredSection !== null && currentStoredSection !== undefined) {
            const sectionNum = parseInt(currentStoredSection, 10);
            
            if (!isNaN(sectionNum) && sectionNum >= 1 && sectionNum !== assignedSection) {
              console.log(`🔄 Section updated from storage: ${assignedSection} -> ${sectionNum}`);
              setAssignedSection(sectionNum);
              // 🔧 Reset la vérification pour permettre une nouvelle vérification si nécessaire
              setHasCheckedSectionValidity(false);
            }
          }
        } catch (error) {
          console.error('Error checking section update:', error);
        }
      };
      
      checkSectionUpdate();
    }, [assignedSection, riders.length, fetchRidersProgress])
  );

  const handleSectionSelection = async (section) => {
    try {
      hapticService.selection();
      
      const success = await AsyncStorageHelper.setItem('lastSelectedSection', section);
      
      if (success) {
        setAssignedSection(section);
        setShowSectionSelection(false);
        setHasCheckedSectionValidity(true); // 🔧 Marquer comme vérifié
        console.log('Section assigned:', section);
      } else {
        alertService.showAlert('Error', 'Failed to save your assigned section. Please try again.');
      }
    } catch (error) {
      console.error('Error saving assigned section:', error);
      alertService.showAlert('Error', 'Failed to save your assigned section. Please try again.');
    }
  };

  const handleSectionChangeFromModal = async (section) => {
    try {
      hapticService.selection();
      
      const success = await AsyncStorageHelper.setItem('lastSelectedSection', section);
      
      if (success) {
        setAssignedSection(section);
        setShowSectionChangeModal(false);
        console.log('Section changed to:', section);
      } else {
        alertService.showAlert('Error', 'Failed to save your assigned section. Please try again.');
      }
    } catch (error) {
      console.error('Error saving assigned section:', error);
      alertService.showAlert('Error', 'Failed to save your assigned section. Please try again.');
    }
  };

  const handleRetry = async () => {
    if (isDisabled || errorType === 'session') return;
    
    hapticService.button();
    try {
      await fetchRiders();
      resetCountdown();
    } catch (e) {
      hapticService.error();
      alertService.showAlert('Error', 'Failed to retry. Please check your connection and try again.');
    }
  };

  const clearSearch = () => {
    hapticService.button();
    setSearchText('');
  };
  
  const handleRiderPress = (rider) => {
    hapticService.listItem();
    navigation.navigate('Scoring', {
      competitionName,
      riderName: rider.name,
      riderNumber: rider.number,
      riderCountry: rider.country,
      laps: lapsAndSections.laps,
      sections: lapsAndSections.sections,
      assignedSection: assignedSection
    });
  };
  
  // 🆕 Version mise à jour de renderRiderItem avec indicateur de progression
  const renderRiderItem = ({ item }) => {
    const progress = ridersProgress[item.number];
    const currentLap = progress?.currentLap;
    const hasStarted = progress?.hasStarted || false;

    return (
      <TouchableOpacity
        style={styles.riderItem}
        onPress={() => handleRiderPress(item)}
      >
        <View style={styles.numberCircle}>
          <Text style={styles.riderNumber}>{item.number}</Text>
        </View>
        <View style={styles.riderInfo}>
          <Text style={styles.riderName}>{item.name}</Text>
          <Text style={styles.riderCountry}>{item.country}</Text>
        </View>
        
        {/* 🆕 Indicateur de progression */}
        {hasStarted && currentLap && (
          <View style={[
            styles.progressIndicator,
            progress?.isComplete && styles.progressIndicatorComplete
          ]}>
            <Text style={styles.progressText}>L{currentLap}</Text>
          </View>
        )}
        
        <Ionicons name="chevron-forward" size={22} color="#0066cc" />
      </TouchableOpacity>
    );
  };

  const getErrorMessage = () => {
    if (errorType === 'session') {
      return 'Session expired. Please sign in again.';
    }
    return error;
  };

  const shouldShowRetry = error && errorType && errorType !== 'session';

  // Affichage de la vérification/chargement
  if (isCheckingSection || isLoadingCourseData) {
    return (
      <SafeAreaView style={styles.container} edges={['bottom']}>
        <LoadingScreen message="Loading course information..." variant="content" />
      </SafeAreaView>
    );
  }

  // Affichage de la sélection de section initiale
  if (showSectionSelection) {
    return (
      <SafeAreaView style={styles.container} edges={['bottom']}>
        <View style={styles.sectionSelectionContainer}>
          <View style={styles.sectionSelectionHeader}>
            {/* 🆕 NOUVELLE ICÔNE PIN */}
            <View style={styles.modalIconContainer}>
              <Ionicons name="location" size={48} color="#0066cc" />
            </View>
            <Text style={styles.sectionSelectionTitle}>Select Your Assigned Section</Text>
            
            {/* 🆕 Message si secteur invalide */}
            {assignedSection && lapsAndSections.availableSections && 
             !lapsAndSections.availableSections.includes(assignedSection) && (
              <View style={styles.invalidSectionMessage}>
                <Text style={styles.invalidSectionText}>
                  Section S{assignedSection} is not available in this course. Please select a new section:
                </Text>
              </View>
            )}
            
          </View>
          
          <View style={styles.sectionButtonsGrid}>
            {/* 🔧 Utiliser availableSections */}
            {(lapsAndSections.availableSections || []).map(section => (
              <TouchableOpacity
                key={`section-${section}`}
                style={styles.sectionButton}
                onPress={() => handleSectionSelection(section)}
              >
                <Text style={styles.sectionButtonText}>S{section}</Text>
              </TouchableOpacity>
            ))}
          </View>
          
          <View style={styles.sectionSelectionInfo}>
            <Ionicons name="information-circle-outline" size={20} color="#0066cc" />
            <Text style={styles.sectionSelectionInfoText}>
              You can reset this choice from Settings or select a different section from the scoring screen.
            </Text>
          </View>
        </View>
      </SafeAreaView>
    );
  }
  
  // Affichage principal avec liste des pilotes
  return (
    <SafeAreaView style={styles.container} edges={['bottom']}>
      {/* Modal de changement de section */}
      <Modal
        visible={showSectionChangeModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowSectionChangeModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            {/* 🆕 NOUVELLE ICÔNE PIN */}
            <View style={styles.modalIconContainer}>
              <Ionicons name="location" size={48} color="#0066cc" />
            </View>
            <Text style={styles.modalTitle}>
              Change your assigned section
            </Text>
            
            <View style={styles.modalButtonsGrid}>
              {/* 🔧 Utiliser availableSections */}
              {(lapsAndSections.availableSections || []).map(section => (
                <TouchableOpacity
                  key={`modal-section-${section}`}
                  style={[
                    styles.modalSectionButton,
                    section === assignedSection && styles.modalCurrentAssignedButton
                  ]}
                  onPress={() => handleSectionChangeFromModal(section)}
                >
                  <Text style={[
                    styles.modalSectionButtonText,
                    section === assignedSection && styles.modalCurrentAssignedButtonText
                  ]}>
                    S{section}
                  </Text>
                  {section === assignedSection && (
                    <View style={styles.modalCurrentAssignedIndicator}>
                      <View style={styles.assignedSectionDot} />
                    </View>
                  )}
                </TouchableOpacity>
              ))}
            </View>
            
            {assignedSection && (
              <View style={styles.modalLegendCentered}>
                <View style={styles.assignedSectionDot} />
                <Text style={styles.modalLegendText}>Your current assigned section</Text>
              </View>
            )}
            
            <TouchableOpacity
              style={styles.modalCancelButtonCentered}
              onPress={() => {
                hapticService.button();
                setShowSectionChangeModal(false);
              }}
            >
              <Text style={styles.modalCancelButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <TouchableOpacity 
            style={styles.assignedSectionButton}
            onPress={() => {
              hapticService.button();
              setShowSectionChangeModal(true);
            }}
          >
            <View style={styles.sectionButtonContent}>
              <Ionicons name="location" size={20} color="#fff" />
              <Text style={styles.assignedSectionText}>
                {assignedSection ? `${assignedSection}` : 'No Section'}
              </Text>
            </View>
            <View style={styles.assignedSectionDot} />
            <Text style={styles.tapToChangeText}>Tap to change</Text>
          </TouchableOpacity>
        </View>
        
        <View style={styles.headerRight}>
          <Text style={styles.competitionStats}>
            {lapsAndSections.laps} laps • {lapsAndSections.availableSections?.length || lapsAndSections.sections} sections
          </Text>
        </View>
      </View>

      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{getErrorMessage()}</Text>
          {errorType === 'session' ? (
            <TouchableOpacity
              style={styles.signInButton}
              onPress={() => {
                hapticService.button();
                signOut();
              }}
            >
              <Text style={styles.signInText}>Sign In</Text>
            </TouchableOpacity>
          ) : (
            shouldShowRetry && (
              <TouchableOpacity
                style={[styles.retryButton, isDisabled && styles.retryButtonDisabled]}
                onPress={handleRetry}
                disabled={isDisabled}
              >
                <Text style={[styles.retryText, isDisabled && styles.retryTextDisabled]}>
                  {getButtonText()}
                </Text>
              </TouchableOpacity>
            )
          )}
        </View>
      )}

      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color="#666" style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search riders..."
          value={searchText}
          onChangeText={setSearchText}
          autoCapitalize="none"
          autoCorrect={false}
        />
        {searchText.length > 0 && (
          <TouchableOpacity onPress={clearSearch} style={styles.clearButton}>
            <Ionicons name="close-circle" size={20} color="#999" />
          </TouchableOpacity>
        )}
      </View>

      {isLoading ? (
        <LoadingScreen message="Loading riders..." variant="content" />
      ) : (
        <FlatList
          data={filteredRiders}
          renderItem={renderRiderItem}
          keyExtractor={(item) => `${item.number}-${item.name}`}
          style={styles.ridersList}
          ListEmptyComponent={
            <View style={styles.emptyList}>
              <Ionicons name="people-outline" size={64} color="#ccc" />
              <Text style={styles.emptyText}>
                {searchText ? 'No riders found matching your search.' : 'No riders found.'}
              </Text>
            </View>
          }
          showsVerticalScrollIndicator={false}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  modalIconContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  // STYLES POUR SÉLECTION DE SECTION INITIALE
  sectionSelectionContainer: {
    flex: 1,
    padding: 24,
    justifyContent: 'center',
  },
  sectionSelectionHeader: {
    alignItems: 'center',
    marginBottom: 32,
  },
  sectionSelectionTitle: {
    fontSize: 24,
    
    color: '#333',
    textAlign: 'center',
    marginBottom: 16,
    fontFamily: 'Montserrat_700Bold',
  },
  sectionSelectionSubtitle: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    fontFamily: 'Montserrat_400Regular',
    marginBottom: 8,
  },
  competitionName: {
    fontSize: 20,
    
    color: '#0066cc',
    textAlign: 'center', 
    fontFamily: 'Montserrat_700Bold',
    marginBottom: 6,
  },
  competitionDetails: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    fontFamily: 'Montserrat_500Medium',
  },
  
  // 🆕 Styles pour message d'avertissement
  invalidSectionMessage: {
    backgroundColor: '#fff3e0',
    padding: 12,
    marginVertical: 8,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#ff9800',
    marginHorizontal: 16,
  },
  invalidSectionText: {
    color: '#e65100',
    fontFamily: 'Montserrat_500Medium',
    textAlign: 'center',
    fontSize: 14,
  },
  
  sectionButtonsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 32,
    gap: 16,
  },
  sectionButton: {
    backgroundColor: '#f0f0f0',
    borderRadius: 12,
    width: 64,
    height: 64,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#e0e0e0',
  },
  sectionButtonText: {
    color: '#555',
    fontSize: 24,
    
    fontFamily: 'Montserrat_700Bold',
  },
  sectionSelectionInfo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#e3f2fd',
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    borderLeftWidth: 4,
    borderLeftColor: '#0066cc',
    marginLeft: 50,
    marginRight: 50,
    marginTop: 20,
  },
  sectionSelectionInfoText: {
    fontSize: 14,
    color: '#0066cc',
    marginLeft: 12,
    lineHeight: 20,
    flex: 1,
    fontFamily: 'Montserrat_400Regular',
  },
  
  // STYLES POUR HEADER PRINCIPAL
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerLeft: {
    flex: 0,
    marginRight: 12,
    maxWidth: 160, // 🔧 Limite la largeur max
  },
  headerRight: {
    flex: 1, // 🔧 Prend l'espace restant
    alignItems: 'flex-end',
    zIndex: -1000,
  },
  assignedSectionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0066cc',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    minWidth: 160, // 🔧 Plus large
    flexShrink: 0, // 🔧 Empêche la compression
  },
  assignedSectionText: {
    fontSize: 18, // 🔧 Plus grand
    
    color: '#fff', // 🔧 Texte blanc
    marginRight: 8, // 🔧 Plus d'espace
    marginLeft: 3, // 🆕 NOUVEAU : Espace entre l'icône et le texte
    fontFamily: 'Montserrat_700Bold',
  },
  tapToChangeText: {
    fontSize: 11, // 🔧 Légèrement plus petit
    color: '#fff', // 🔧 Texte blanc
    marginLeft: 8,
    fontFamily: 'Montserrat_400Regular',
    flexShrink: 0, // 🔧 Empêche le retour à la ligne
  },
  sectionButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  competitionHeader: {
    fontSize: 18,
    
    color: '#333',
    textAlign: 'right',
    fontFamily: 'Montserrat_700Bold',
  },
  competitionStats: {
    fontSize: 14,
    color: '#666',
    textAlign: 'right',
    fontFamily: 'Montserrat_400Regular',
  },
  
  // STYLES POUR MODAL DE CHANGEMENT DE SECTION
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    maxWidth: 500,
    width: '100%',
    maxHeight: '80%',
  },
  modalTitle: {
    fontSize: 22,
    
    color: '#333',
    textAlign: 'center',
    marginBottom: 32,
    fontFamily: 'Montserrat_700Bold',
  },
  modalButtonsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
    gap: 16,
  },
  modalSectionButton: {
    backgroundColor: '#f0f0f0',
    borderRadius: 12,
    width: 64,
    height: 64,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#e0e0e0',
    position: 'relative',
  },
  modalCurrentAssignedButton: {
    backgroundColor: 'rgba(244, 67, 54, 0.1)',
    borderColor: '#f44336',
  },
  modalSectionButtonText: {
    color: '#555',
    
    fontSize: 24,
    fontFamily: 'Montserrat_700Bold',
  },
  modalCurrentAssignedButtonText: {
    color: '#555',
  },
  modalCurrentAssignedIndicator: {
    position: 'absolute',
    top: 6,
    right: 6,
  },
  assignedSectionDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#f44336',
  },
  modalLegendCentered: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  modalLegendText: {
    fontSize: 14,
    color: '#555',
    marginLeft: 8,
    fontFamily: 'Montserrat_400Regular',
  },
  modalCancelButtonCentered: {
    backgroundColor: '#6c757d',
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 24,
    alignSelf: 'center',
  },
  modalCancelButtonText: {
    color: '#fff',
    fontSize: 16,
    
    fontFamily: 'Montserrat_600SemiBold',
  },
  
  // STYLES POUR RECHERCHE ET LISTE
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    margin: 16,
    marginBottom: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    height: 46,
    fontSize: 16,
    fontFamily: 'Montserrat_400Regular',
    outline: 'none',
  },
  clearButton: {
    padding: 4,
    marginLeft: 8,
  },
  errorContainer: {
    margin: 16,
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#ffebee',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  errorText: {
    color: '#d32f2f',
    flex: 1,
    fontFamily: 'Montserrat_400Regular',
  },
  retryButton: {
    backgroundColor: '#0066cc',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
  },
  retryButtonDisabled: {
    backgroundColor: '#cccccc',
  },
  retryText: {
    color: '#fff',
    
    fontFamily: 'Montserrat_700Bold',
  },
  retryTextDisabled: {
    color: '#999999',
  },
  signInButton: {
    backgroundColor: '#0066cc',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
  },
  signInText: {
    color: '#fff',
    
    fontFamily: 'Montserrat_700Bold',
  },
  ridersList: {
    padding: 16,
    paddingTop: 8,
  },
  riderItem: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  numberCircle: {
    width: 50,
    height: 50,
    borderRadius: 30,
    backgroundColor: '#0066cc',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  riderNumber: {
    color: '#fff',
    
    fontSize: 16,
    fontFamily: 'Montserrat_700Bold',
  },
  riderInfo: {
    flex: 1,
  },
  riderName: {
    fontSize: 18,
    
    color: '#333',
    marginBottom: 4,
    fontFamily: 'Montserrat_600SemiBold',
  },
  riderCountry: {
    fontSize: 14,
    color: '#666',
    fontFamily: 'Montserrat_400Regular',
  },
  
  // 🆕 NOUVEAUX STYLES POUR L'INDICATEUR DE PROGRESSION
  progressIndicator: {
    backgroundColor: '#ff9800', // 🔄 Orange comme ScoringScreen (in progress)
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 8,
    minWidth: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressIndicatorComplete: {
    backgroundColor: '#4caf50', // 🆕 Vert quand tous les laps sont terminés
  },
  progressText: {
    color: '#fff',
    fontSize: 12,
    
    fontFamily: 'Montserrat_700Bold',
  },
  
  emptyList: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyText: {
    marginTop: 16,
    fontSize: 16,
    color: '#888',
    textAlign: 'center',
    fontFamily: 'Montserrat_400Regular',
  },
});

export default SelectionScreen;